import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Navbar from './components/Navbar';
import AutoLogout from './components/AutoLogout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LoadingProvider, useLoading } from './context/LoadingContext';
import { fetchCSRFToken } from './utils/csrf';
import Layout from './components/Layout';
import Loading from './components/Loading';
import './App.css';
import { Navigate } from 'react-router-dom';
import image1 from './assets/images/dockids.JPG';

// Lazy load components
const LoginPage = lazy(() => import('./components/LoginPage'));
const HomePage = lazy(() => import('./components/HomePage'));
const CheckEligibility = lazy(() => import('./components/CheckEligibility'));
const EligibilityResults = lazy(() => import('./components/EligibilityResults'));
const AddUtilization = lazy(() => import('./components/AddUtilization'));
const AddUtilizationForm = lazy(() => import('./components/AddUtilizationForm'));
const AddLabUtilizationForm = lazy(() => import('./components/LabUtilizationForm'));
const UtilizationResults = lazy(() => import('./components/UtilizationResults'));
const ProviderRoster = lazy(() => import('./pages/ProviderRoster'));
const UploadProviders = lazy(() => import('./pages/UploadProviders'));
const UtilizationRoster = lazy(() => import('./pages/UtilizationRoster'));
const UtilizationList = lazy(() => import('./pages/UtilizationList'));
const LabUtilizationList = lazy(() => import('./pages/LabUtilizationList'));
const ProviderUtilizationList = lazy(() => import('./pages/ProviderUtilizationList'));
const ProviderLabUtilizationList = lazy(() => import('./pages/ProviderLabUtilizationList'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const PricingManagement = lazy(() => import('./pages/PricingManagement'));
const MemberRoster = lazy(() => import('./pages/MemberRoster'));
const AddRegion = lazy(() => import('./pages/AddRegion'));
const RegionsList = lazy(() => import('./pages/RegionsList'));
const AddType = lazy(() => import('./pages/AddType'));
const TypesList = lazy(() => import('./pages/TypesList'));
const AddProvider = lazy(() => import('./pages/AddProvider'));
const EditProvider = lazy(() => import('./pages/EditProvider'));
const ProviderListDB = lazy(() => import('./pages/ProviderListDB'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const DeleteUser = lazy(() => import('./pages/DeleteUser'));
const ProviderUsers = lazy(() => import('./pages/ProviderUsers'));
const ProductPriceList = lazy(() => import('./pages/ProductPriceList'));
const ProductPriceList2025 = lazy(() => import('./pages/ProductPriceList_2025'));
const ProductPriceList2026 = lazy(() => import('./pages/ProductPriceList_2026'));
const UploadPage = lazy(() => import('./pages/UploadPage'));
const UploadPage2025 = lazy(() => import('./pages/UploadPage_2025'));
const UploadPage2026 = lazy(() => import('./pages/UploadPage_2026'));
const PremiumCalculator = lazy(() => import('./pages/PremiumCalculator'));
const TransactionForm = lazy(() => import('./pages/TransactionForm'));
const CoremedProducts = lazy(() => import('./pages/CoremedProducts'));
const UploadProducts = lazy(() => import('./pages/UploadProducts'));
const BusinessDevelopment = lazy(() => import('./pages/Business_Development'));
const PriceQueryList = lazy(() => import('./pages/PriceQueryList'));
const UtilizationSummary = lazy(() => import('./pages/UtilizationSummary'));
const UtilizationSummary2 = lazy(() => import('./pages/UtilizationSummary2'));
const UtilizationDetail = lazy(() => import('./pages/UtilizationDetail'));
const TypePayoutTable = lazy(() => import('./pages/TypePayoutTable'));
const UploadUtilizationPage = lazy(() => import('./pages/UploadUtilizationPage'));
const PharmacyUtilizationSummary = lazy(() => import('./pages/PharmacyUtilizationSummary'));
const DpcMembersList = lazy(() => import('./pages/DpcMembersList'));
const DpcSummary = lazy(() => import('./pages/DpcSummary'));
const TermedMembers = lazy(() => import('./pages/TermedMembers'));
const ProviderUserMembers = lazy(() => import('./pages/ProviderUserMembers'));

function App() {
  // CSRF token initialization
  useEffect(() => {
    const initializeCSRF = async () => {
      try {
        await fetchCSRFToken();
      } catch (error) {
        console.error('CSRF token fetch failed:', error);
      }
    };
    initializeCSRF();
  }, []);

  return (
    <LoadingProvider>
      <AuthProvider>
        <Router>
          <AutoLogout />
          <LoadingOverlay />
          <div>
            <Navbar />
            <Suspense fallback={<div>Loading...</div>}>
              <Routes>
                <Route path="/" element={<LoginPage />} />
                <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                <Route path="/check-eligibility" element={<ProtectedRoute><Layout><CheckEligibility /></Layout></ProtectedRoute>} />
                <Route path="/results" element={<ProtectedRoute><Layout><EligibilityResults /></Layout></ProtectedRoute>} />
                <Route path="/add-utilization" element={<ProtectedRoute><Layout><AddUtilization /></Layout></ProtectedRoute>} />
                <Route path="/add-utilization-form" element={<ProtectedRoute><Layout><AddUtilizationForm /></Layout></ProtectedRoute>} />
                <Route path="/add-lab-utilization-form" element={<ProtectedRoute><Layout><AddLabUtilizationForm /></Layout></ProtectedRoute>} />
                <Route path="/utilization-results" element={<ProtectedRoute><Layout><UtilizationResults /></Layout></ProtectedRoute>} />
                <Route path="/provider-roster" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><ProviderRoster /></Layout></ProtectedRoute>} />
                <Route path="/add-provider" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><AddProvider /></Layout></ProtectedRoute>} />
                <Route path="/edit-provider/:id" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><EditProvider /></Layout></ProtectedRoute>} />
                <Route path="/provider-list-db" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><ProviderListDB /></Layout></ProtectedRoute>} />
                <Route path="/utilization-roster" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UtilizationRoster /></Layout></ProtectedRoute>} />
                <Route path="/view-utilization" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UtilizationList /></Layout></ProtectedRoute>} />
                <Route path="/view-lab-utilization" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><LabUtilizationList /></Layout></ProtectedRoute>} />
                <Route path="/provider-utilization-list" element={<ProtectedRoute><Layout><ProviderUtilizationList /></Layout></ProtectedRoute>} />
                <Route path="/provider-lab-utilization-list" element={<ProtectedRoute><Layout><ProviderLabUtilizationList /></Layout></ProtectedRoute>} />
                <Route path="/user-management/*" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UserManagement /></Layout></ProtectedRoute>} />
                <Route path="/pricing-roster" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><PricingManagement /></Layout></ProtectedRoute>} />
                <Route path="/member-roster" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><MemberRoster /></Layout></ProtectedRoute>} />
                <Route path="/add-region" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><AddRegion /></Layout></ProtectedRoute>} />
                <Route path="/regions" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><RegionsList /></Layout></ProtectedRoute>} />
                <Route path="/add-type" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><AddType /></Layout></ProtectedRoute>} />
                <Route path="/types" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><TypesList /></Layout></ProtectedRoute>} />
                <Route path="/user-profile" element={<ProtectedRoute><Layout><UserProfile /></Layout></ProtectedRoute>} />
                <Route path="/delete_user/:userId" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><DeleteUser /></Layout></ProtectedRoute>} />
                <Route path="/provider-users" element={<ProtectedRoute><Layout><ProviderUsers /></Layout></ProtectedRoute>} />
                <Route path="/product-prices" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><ProductPriceList /></Layout></ProtectedRoute>} />
                <Route path="/product-prices-2025" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><ProductPriceList2025 /></Layout></ProtectedRoute>} />
                <Route path="/product-prices-2026" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><ProductPriceList2026 /></Layout></ProtectedRoute>} />
                <Route path="/upload-product-prices" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UploadPage /></Layout></ProtectedRoute>} />
                <Route path="/upload-product-prices-2025" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UploadPage2025 /></Layout></ProtectedRoute>} />
                <Route path="/upload-product-prices-2026" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UploadPage2026 /></Layout></ProtectedRoute>} />
                <Route path="/premium-calculator" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><PremiumCalculator /></Layout></ProtectedRoute>} />
                <Route path="/upload-provider-roster" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UploadProviders /></Layout></ProtectedRoute>} />
                <Route path="/transaction-form" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><TransactionForm /></Layout></ProtectedRoute>} />
                <Route path="/coremed-products" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><CoremedProducts /></Layout></ProtectedRoute>} />
                <Route path="/upload-products" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UploadProducts /></Layout></ProtectedRoute>} />
                <Route path="/business-development" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><BusinessDevelopment /></Layout></ProtectedRoute>} />
                <Route path="/price-query-list" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><PriceQueryList /></Layout></ProtectedRoute>} />
                <Route path="/utilization-summary" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UtilizationSummary /></Layout></ProtectedRoute>} />
                <Route path="/utilization-summary2" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UtilizationSummary2 /></Layout></ProtectedRoute>} />
                <Route path="/utilization-detail" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UtilizationDetail /></Layout></ProtectedRoute>} />
                <Route path="/payouts" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><TypePayoutTable /></Layout></ProtectedRoute>} />
                <Route path="/uploadutilization" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><UploadUtilizationPage /></Layout></ProtectedRoute>} />
                <Route path="/pharmacyutilization" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><PharmacyUtilizationSummary /></Layout></ProtectedRoute>} />
                <Route path="/dpc-members" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><DpcMembersList /></Layout></ProtectedRoute>}/>
                <Route path="/dpc-summary" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><DpcSummary /></Layout></ProtectedRoute>}/>
                <Route path="/dpc-members/:provider" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><DpcMembersList/></Layout></ProtectedRoute>}/>
                <Route path="/dpc-termed" element={<ProtectedRoute allowedUserType="CoreMed Admin"><Layout><TermedMembers /></Layout></ProtectedRoute>}/>
                <Route path="/my-members" element={<ProtectedRoute allowedUserType="Provider User"><Layout><ProviderUserMembers /></Layout></ProtectedRoute>}/>
              </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </LoadingProvider>
  );
}

// ProtectedRoute component to handle authentication checks
function ProtectedRoute({ children, allowedUserType }) {
  const { isAuthenticated, userType } = useAuth();

  // If not authenticated, redirect to the login page.
  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  // If allowedUserType is specified and the user's type doesn't match, show an error message.
  if (allowedUserType && userType !== allowedUserType) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Unauthorized Access</h2>
        <p>You do not have permission to view this page.</p>
        <img src={image1} alt="Dockids" className="home-image" />
      </div>
    );
  }

  // Otherwise, render the child component(s)
  return children;
}

// Loading overlay to show loading spinner when necessary
function LoadingOverlay() {
  const { isLoading } = useLoading();
  return isLoading ? <Loading /> : null;
}

export default App;
